/**
 * The opening turn for a Foundation Data v2 curation session.
 *
 * A foundation session used to get `buildGreetingPrompt` — the CARBON greeting — because the greeting effect
 * had no `sessionKind` gate. Every clause of it was wrong here: it told the agent it was "mapping my data
 * into a carbon data task — you do not know which part of it yet", to "ask me for my file", and to "Do NOT
 * call any tools". This session's rows are pushed in at creation and it has no file to ask for, and the
 * foundation system prompt says in as many words never to ask the user to upload them.
 *
 * It also carries the OFFER. The agent states how much of the organisation's wanted structure the extracted
 * figures already cover and offers to normalise the rest — it does not spend a multi-minute turn doing it
 * unasked, which is what the carbon side's Map & review stage does and what nobody asked for here.
 *
 * @param {{year: (number|string|null), workstream: (string|null), coverage: (object|null)}} params
 *   `coverage` is the `foundation-coverage` response, or null when it could not be read.
 * @returns {{text: string, display: string}} `text` goes to the model; `display` is shown instead.
 */
export function buildFoundationGreetingPrompt({ year = null, workstream = null, coverage = null } = {}) {
  const destination = [workstream, year].filter(Boolean).join(" ") || "this reporting year";

  const lines = [
    `I have just opened a chat to tidy up the figures extracted for ${destination}.`,
    "The extracted rows are already in the session — I have not uploaded anything and there is nothing to upload.",
    "",
    "Open the conversation: introduce yourself in two or three short lines — what you do here, and that you",
    `are helping me get ${destination} into shape for saving.`,
  ];

  /*
    The numbers come from the coverage endpoint, not from the model counting: the same rule the save applies,
    so the figure the user is told here is the figure the save dialog will report. Omitted entirely rather
    than guessed when it could not be read — a made-up number here is worse than no number, because the user
    would reconcile it against the dialog later and find it wrong.
  */
  if (coverage && coverage.wanted > 0) {
    lines.push(
      "",
      `Then state, in one line: ${coverage.filled} of ${coverage.wanted} of the datapoints this organisation`,
      `asked for are filled` +
        (coverage.off_schema > 0
          ? `, and ${coverage.off_schema} extracted figure${
              coverage.off_schema === 1 ? "" : "s"
            } match none of them`
          : "") +
        ".",
    );
    if (coverage.unit_mismatches?.length > 0) {
      lines.push(
        `Mention that ${coverage.unit_mismatches.length} figure${
          coverage.unit_mismatches.length === 1 ? " is" : "s are"
        } in a different unit than expected.`,
      );
    }
    lines.push(
      "",
      "Then OFFER to go through them with me — normalise the names towards the wanted datapoints, tidy the",
      "units' spelling, group what is left — and ask whether I want you to start. Do not start yet.",
    );
  } else {
    // No structure, or it could not be read. Offer the same help without pretending to know a number.
    lines.push(
      "",
      "Then offer to go through the figures with me — tidy the units, the scopes and the names — and ask what",
      "I want done first. Do not start yet.",
    );
  }

  lines.push(
    "",
    // Named explicitly, because the rule it overrides is real and a future reader of system.py should be
    // able to find out why one turn ignores it.
    'This turn is the one exception to "No filler. Do not add introductory paragraphs" in your instructions:',
    "an introduction is the entire purpose of it. Keep it warm and short.",
    "",
    "Do NOT call any tools on this turn — everything above is already in your context, and profiling the",
    "rows is step 2 of your workflow, once I have said what I want. Do NOT ask me for a file.",
  );

  return { display: "Starting a new session…", text: lines.join("\n") };
}

export default buildFoundationGreetingPrompt;
